// src/components/Admin/VirtualScenes/SceneItems.js
import React, { useState } from "react";
import { Button, Icon } from "semantic-ui-react";
import { App as AntApp, Button as AntButton, Popconfirm, Tooltip } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { BasicModal } from "../../../Shared";
import { MultiStepForm } from "../SceneForm";
import {
  VirtualScenes as VirtualScenesApi,
  MozillaHubRoomsApi,
} from "../../../../api";
import { useAuth } from "../../../../hooks";
import { NEW_EDUCAMAKER } from "../../../../ui/featureFlags";
import { useTranslation } from "react-i18next";
import styles from "./SceneItems.module.scss";
import "./SceneItems.scss";

const virtualSceneController = new VirtualScenesApi();
const mozillaHubRoomsApi = new MozillaHubRoomsApi();

// Origen del Hub GCP (mismo que usa el flujo de evaluacion en SceneIframe.js).
const HUB_ORIGIN = "https://educa360.es";

// Extrae el scene_id corto de una URL de escena (.../scenes/<id>). Devuelve null si la
// URL no es una escena de Hubs (sala directa o enlace externo) -> se abre tal cual.
function extractSceneId(url) {
  const m = String(url || "").match(/\/scenes\/([^/?#]+)/);
  return m ? m[1] : null;
}

// Color del punto por categoria (categorico, no semantico de marca).
const CATEGORY_COLORS = {
  "CIENCIAS HUMANAS": "#7b61ff",
  "CIENCIAS NATURALES": "#16a934",
  HISTORIA: "#c26b2e",
  MATEMÁTICAS: "#2d7ff9",
  ARTE: "#b5179e",
  "VIDA SALUDABLE": "#00a75a",
  INGLÉS: "#0aa2c0",
  EMPRENDIMIENTO: "#d9a100",
  TECNOLOGÍA: "#475569",
  ROBÓTICA: "#e8590c",
};

function catColor(cat) {
  return CATEGORY_COLORS[String(cat || "").toUpperCase()] || "#8c9aa5";
}

export function SceneItems(props) {
  const { scene, onReload } = props;
  const { t } = useTranslation("scenes");
  const { accessToken } = useAuth();
  const { message } = AntApp.useApp();
  const [showModal, setShowModal] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [opening, setOpening] = useState(false);

  // SEC-HUB-02: "Entrar en escena" NO debe abrir la URL cruda de la escena (permanente,
  // robable). Genera una sala efimera con caducidad 3h (createRoom marca userData -> el
  // actuador la cierra al vencer) y la abre en pestana nueva. Mismo flujo que la evaluacion.
  const handleView = async () => {
    const sceneId = extractSceneId(scene.urlsala);

    // No es una escena /scenes/ (sala directa o enlace externo): abrir tal cual.
    // No pasa por /roomshub/load, asi que anotamos la apertura con la baliza.
    if (!sceneId) {
      mozillaHubRoomsApi.trackRoomOpen(accessToken, {
        sceneRefId: scene.refId,
        url: scene.urlsala,
        title: scene.title,
        source: "educamaker",
      });
      window.open(scene.urlsala, "_blank", "noopener,noreferrer");
      return;
    }

    // Abrimos la pestana YA (dentro del gesto de click) para evitar el bloqueo de popups;
    // la rellenamos cuando el backend cree la sala. Cortamos opener (anti tabnabbing).
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;

    try {
      setOpening(true);
      // `source` clasifica la apertura en el dashboard admin: desde el catalogo
      // de escenas propias / Educamaker es un OJEO, no uso con alumnos.
      const result = await mozillaHubRoomsApi.LoadRoom(accessToken, sceneId, {
        source: "educamaker",
        sceneRefId: scene.refId,
      });
      const roomUrl = `${HUB_ORIGIN}/${result.room.id}?embed_token=${result.room.embedToken}`;
      if (tab) tab.location = roomUrl;
      else window.open(roomUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error al abrir la sala de la escena:", error);
      if (tab) tab.close();
      message.error(t("item.viewError"));
    } finally {
      setOpening(false);
    }
  };

  const openUpdateScene = () => {
    setIsUpdateMode(true);
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await virtualSceneController.deleteVirtualScene(accessToken, scene.refId);
      message.success(t("item.deleteSuccess"));
      if (typeof onReload === "function") onReload();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error al eliminar la escena:", error);
      message.error(t("item.deleteError"));
    } finally {
      setDeleting(false);
    }
  };

  function formatCategories(categories) {
    return (categories || []).join(" ");
  }

  const cats = Array.isArray(scene.category)
    ? scene.category.filter(Boolean)
    : [];
  const subs = Array.isArray(scene.subcategory)
    ? scene.subcategory.filter(Boolean)
    : [];

  const editModal = (
    <BasicModal
      show={showModal}
      close={() => setShowModal(false)}
      title={
        <>
          {t("item.editingTitle")} {scene.title}
          <br />
          {scene.account}
        </>
      }
    >
      <MultiStepForm
        scene={scene}
        onClose={() => setShowModal(false)}
        onReload={onReload}
        isUpdateMode={isUpdateMode}
      />
    </BasicModal>
  );

  // ── Tarjeta nueva (design system verde) ──────────────────────────────────
  if (NEW_EDUCAMAKER) {
    return (
      <>
        <div className={styles.card}>
          <div className={styles.thumb}>
            {scene.bannertopUrl ? (
              <img src={scene.bannertopUrl} alt={scene.title} loading="lazy" />
            ) : (
              <PictureOutlined className={styles.thumbIcon} />
            )}
          </div>

          <div className={styles.body}>
            <h2 className={styles.title} title={scene.title}>
              {scene.title}
            </h2>

            {cats.length ? (
              <div className={styles.cats}>
                {cats.map((c) => (
                  <span className={styles.catTag} key={c}>
                    <span
                      className={styles.dot}
                      style={{ background: catColor(c) }}
                    />
                    {c}
                  </span>
                ))}
              </div>
            ) : null}

            {subs.length ? (
              <p className={styles.sub}>{subs.join(", ")}</p>
            ) : null}

            <div className={styles.actions}>
              <AntButton
                type="primary"
                icon={<EyeOutlined />}
                onClick={handleView}
                loading={opening}
              >
                {t("item.viewButton")}
              </AntButton>
              <AntButton
                icon={<EditOutlined />}
                onClick={openUpdateScene}
                className={styles.editBtn}
              >
                {t("item.editButton")}
              </AntButton>

              <Popconfirm
                title={t("item.deleteConfirmTitle")}
                description={t("item.deleteConfirmDesc", {
                  title: scene.title,
                })}
                okText={t("item.deleteButton")}
                cancelText={t("item.deleteCancel")}
                okButtonProps={{ danger: true, loading: deleting }}
                onConfirm={handleDelete}
              >
                <Tooltip title={t("item.deleteButton")}>
                  <AntButton
                    danger
                    icon={<DeleteOutlined />}
                    loading={deleting}
                    aria-label={t("item.deleteButton")}
                  />
                </Tooltip>
              </Popconfirm>
            </div>
          </div>
        </div>

        {editModal}
      </>
    );
  }

  // ── Tarjeta legacy (Semantic UI) ─────────────────────────────────────────
  return (
    <>
      <div className="scene-item">
        <div className="scene-item__image">
          <img src={scene.bannertopUrl} alt={scene.title} loading="lazy" />
        </div>
        <div className="scene-item__content">
          <h2 className="scene-item__title">{scene.title}</h2>
          <p className="scene-item__categories">
            {formatCategories(scene.category)}
          </p>
          <p className="scene-item__categories">
            {formatCategories(scene.subcategory)}
          </p>
          <div className="scene-item__buttons">
            {/* Ver */}
            <Button
              icon
              onClick={handleView}
              loading={opening}
              className="btn-icon scene-item__btn-view"
            >
              <Icon name="eye" />
            </Button>
            {/* Editar */}
            <Button
              icon
              onClick={openUpdateScene}
              className="btn-icon scene-item__btn-edit"
            >
              <Icon name="pencil" />
            </Button>
          </div>
        </div>
      </div>

      {editModal}
    </>
  );
}
